import type { WorkOrderChangePriceItem } from 'gql/graphql';
import { AddRatioToActKc2StrategyEnum, WorkOrderPriceStatusEnum, WorkOrderPriceTypeEnum } from 'gql/graphql';

import { useCallback, useMemo } from 'react';

import { WorkOrderChangeMenuActionTypes, WorkOrderOtherOpEnum } from '../../types';

const menuGroups = [
    {
        id: 1,
    },
    {
        id: 2,
    },
];

export const useMenuItemsByRowType = ({
    rowData,
    openModalHandler,
}: {
    rowData: WorkOrderChangePriceItem | null | undefined;
    openModalHandler: (type: any, rowData: any, options?: any) => void;
}) => {
    const runActionByType = useCallback(
        (type: WorkOrderChangeMenuActionTypes, options?: any) => () => {
            openModalHandler(type, rowData, options);
        },
        [openModalHandler, rowData],
    );

    const { rowHasAcceptedCost, canRemoveRow, canExcludeRow } = useMemo(
        () => ({
            canRemoveRow: rowData?.hasAcceptedCost === null,
            canExcludeRow: rowData?.hasAcceptedCost === false,
            rowHasAcceptedCost: rowData?.hasAcceptedCost,
        }),
        [rowData?.hasAcceptedCost],
    );

    const editOperationDisabled = useMemo(
        () =>
            !(
                rowData?.isAddedPrice &&
                !rowData?.isActKC2Price &&
                (rowData?.status === WorkOrderPriceStatusEnum.NewRecord ||
                    rowData?.status === WorkOrderPriceStatusEnum.Draft)
            ),
        [rowData?.isActKC2Price, rowData?.isAddedPrice, rowData?.status],
    );
    const moveVolumesUerDisabled = useMemo(
        () => (rowData?.seasonValues?.length ?? 0) <= 1,
        [rowData?.seasonValues?.length],
    );
    const isDuplicate = useMemo(() => rowData && 'hasDuplicates' in rowData && rowData.hasDuplicates, [rowData]);

    const uerMenu = useMemo(
        () => [
            {
                label: 'Перенести объемы между сезонами',
                groupId: 1,
                action: runActionByType(WorkOrderChangeMenuActionTypes.openMoveVolumesBetweenSeasonsUer),
                disabled: moveVolumesUerDisabled,
            },
            {
                label: 'Редактировать расценку',
                groupId: 1,
                action: runActionByType(WorkOrderChangeMenuActionTypes.editUer),
                disabled: editOperationDisabled,
            },
            {
                label: 'Добавить коэффициент',
                action: () => {
                    if (!rowHasAcceptedCost) {
                        runActionByType(WorkOrderChangeMenuActionTypes.addRatio, { strategy: null })();
                    }
                },
                ...(rowHasAcceptedCost && {
                    menu: [
                        {
                            label: 'Дублирование расценки с расширенным составом коэффициентов',
                            action: runActionByType(WorkOrderChangeMenuActionTypes.addRatio, {
                                strategy: AddRatioToActKc2StrategyEnum.DuplicateWithExtendedCoefficients,
                            }),
                        },
                        {
                            label: 'Корректировка исходной расценки',
                            action: runActionByType(WorkOrderChangeMenuActionTypes.addRatio, {
                                strategy: AddRatioToActKc2StrategyEnum.CorrectOriginal,
                            }),
                        },
                    ],
                }),
                groupId: 1,
            },
            {
                label: 'Добавить МТР',
                action: runActionByType(WorkOrderChangeMenuActionTypes.addMtr),
                groupId: 1,
            },
            {
                label: 'Редактировать примечание',
                groupId: 1,
                action: runActionByType(WorkOrderChangeMenuActionTypes.editNote),
            },
            canExcludeRow
                ? {
                      label: 'Исключить',
                      action: runActionByType(WorkOrderChangeMenuActionTypes.exclude),
                      groupId: 2,
                  }
                : {
                      label: 'Удалить',
                      action: runActionByType(WorkOrderChangeMenuActionTypes.singleDelete),
                      groupId: 2,
                      disabled: !canRemoveRow,
                  },
        ],
        [
            canExcludeRow,
            canRemoveRow,
            editOperationDisabled,
            moveVolumesUerDisabled,
            rowHasAcceptedCost,
            runActionByType,
        ],
    );

    const coefficientMenu = useMemo(
        () => [
            {
                label: 'Редактировать',
                groupId: 1,
                action: runActionByType(WorkOrderChangeMenuActionTypes.editRatio),
                disabled: editOperationDisabled,
            },
            {
                label: 'Редактировать примечание',
                groupId: 1,
                action: runActionByType(WorkOrderChangeMenuActionTypes.editNote),
            },
            canExcludeRow
                ? {
                      label: 'Исключить',
                      action: runActionByType(WorkOrderChangeMenuActionTypes.exclude),
                      groupId: 2,
                  }
                : {
                      label: 'Удалить',
                      action: runActionByType(WorkOrderChangeMenuActionTypes.singleDelete),
                      disabled: !canRemoveRow,
                      groupId: 2,
                  },
        ],
        [canExcludeRow, canRemoveRow, editOperationDisabled, runActionByType],
    );

    const mtrMenu = useMemo(
        () => [
            ...(isDuplicate && rowData?.status === WorkOrderPriceStatusEnum.Draft
                ? [
                      {
                          label: 'Сопоставить с Прейскурантом МТР',
                          action: runActionByType(WorkOrderChangeMenuActionTypes.matchMtr),
                      },
                  ]
                : []),
            {
                label: 'Редактировать',
                groupId: 1,
                action: runActionByType(WorkOrderChangeMenuActionTypes.editMtr),
                disabled: editOperationDisabled,
            },
            {
                label: 'Редактировать примечание',
                groupId: 1,
                action: runActionByType(WorkOrderChangeMenuActionTypes.editNote),
            },
            canExcludeRow
                ? {
                      label: 'Исключить',
                      action: runActionByType(WorkOrderChangeMenuActionTypes.exclude),
                      groupId: 2,
                  }
                : {
                      label: 'Удалить',
                      action: runActionByType(WorkOrderChangeMenuActionTypes.singleDelete),
                      disabled: !canRemoveRow,
                      groupId: 2,
                  },
        ],
        [canExcludeRow, canRemoveRow, editOperationDisabled, isDuplicate, rowData?.status, runActionByType],
    );

    const otherMenu = useMemo(
        () => [
            {
                label: 'Просмотреть',
                groupId: 1,
                action: runActionByType(WorkOrderChangeMenuActionTypes.editOther, {
                    strategy: WorkOrderOtherOpEnum.view,
                }),
            },
            {
                label: 'Изменить распределение',
                action: runActionByType(WorkOrderChangeMenuActionTypes.editOtherDistribution),
                groupId: 1,
            },
            {
                label: 'Редактировать примечание',
                groupId: 1,
                action: runActionByType(WorkOrderChangeMenuActionTypes.editNote),
            },
            canExcludeRow
                ? {
                      label: 'Исключить',
                      action: runActionByType(WorkOrderChangeMenuActionTypes.exclude),
                      groupId: 2,
                  }
                : {
                      label: 'Удалить',
                      action: runActionByType(WorkOrderChangeMenuActionTypes.singleDelete),
                      groupId: 2,
                      disabled: !canRemoveRow,
                  },
        ],
        [canExcludeRow, canRemoveRow, runActionByType],
    );

    const getMenu = useMemo<Partial<Record<WorkOrderPriceTypeEnum | '', any>>>(
        () => ({
            [WorkOrderPriceTypeEnum.Uer]: uerMenu,
            [WorkOrderPriceTypeEnum.Coefficient]: coefficientMenu,
            [WorkOrderPriceTypeEnum.ConstraintCoefficient]: coefficientMenu,
            [WorkOrderPriceTypeEnum.DismantlingCoefficient]: coefficientMenu,
            [WorkOrderPriceTypeEnum.TechPartCoefficient]: coefficientMenu,
            [WorkOrderPriceTypeEnum.Mtr]: mtrMenu,
            [WorkOrderPriceTypeEnum.MtrContractorMaterial]: mtrMenu,
            [WorkOrderPriceTypeEnum.MtrContractorEquipment]: mtrMenu,
            [WorkOrderPriceTypeEnum.MtrCustomerEquipment]: mtrMenu,
            [WorkOrderPriceTypeEnum.MtrCustomerMaterial]: mtrMenu,
            [WorkOrderPriceTypeEnum.Other]: otherMenu,
        }),
        [coefficientMenu, mtrMenu, otherMenu, uerMenu],
    );

    return {
        menuItems: getMenu[rowData?.type ?? ''] ?? [],
        menuGroups,
    };
};
